import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, AlertTriangle, ClipboardList, Plus, Save } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { api, errMsg, fmt, fmtDate, FULFILL, PAY_METHOD } from "../lib/api";
import { StoreOnboarding } from "../components/StoreOnboarding";
import { useCategories } from "../lib/categories";
import PhotoManager from "../components/PhotoManager";
import { Field, PageTitle, Spinner, StatusBadge, btnGhost, btnPrimary, inputCls } from "../components/ui";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bo-card flex items-center gap-4 p-5" data-testid={testid}>
    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-bo-green text-bo-yellow"><Icon className="h-5 w-5" /></span>
    <div><p className="text-2xl font-extrabold">{value}</p><p className="text-xs text-gray-500">{label}</p></div>
  </div>
);

function OfferRow({ o, onSaved, storeId }) {
  const [f, setF] = useState({ price: o.price, old_price: o.old_price || "", stock: o.stock, active: o.active });
  const save = async () => {
    try {
      await api.patch(`/store/offers/${o.id}`, { price: Number(f.price), old_price: f.old_price ? Number(f.old_price) : 0, stock: Number(f.stock), active: f.active });
      toast.success("Oferta atualizada"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cell = "w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm";
  return (
    <tr className="border-b border-gray-100" data-testid={`store-offer-${o.id}`}>
      <td className="py-2 pl-4"><div className="flex items-center gap-2"><img src={o.product?.image} alt="" className="h-9 w-9 rounded-lg object-cover" /><span className="font-medium">{o.product?.name}</span>{o.product?.created_by_store === storeId && <PhotoManager product={o.product} onChanged={onSaved} />}</div></td>
      <td><input type="number" min={0.01} step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={cell} data-testid={`offer-price-input-${o.id}`} /></td>
      <td><input type="number" min={0} step="0.01" value={f.old_price} onChange={(e) => setF({ ...f, old_price: e.target.value })} className={cell} data-testid={`offer-oldprice-input-${o.id}`} /></td>
      <td><input type="number" min={0} value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} className={`${cell} ${f.stock <= 5 ? "border-amber-400" : ""}`} data-testid={`offer-stock-input-${o.id}`} /></td>
      <td className="text-center"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid={`offer-active-checkbox-${o.id}`} /></td>
      <td className="pr-4 text-right"><button onClick={save} className={`${btnGhost} px-3 py-1.5`} data-testid={`offer-save-btn-${o.id}`}><Save className="h-3.5 w-3.5" />Guardar</button></td>
    </tr>
  );
}

function AddOffer({ onSaved, existing }) {
  const CATEGORIES = useCategories();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("existing");
  const [catalog, setCatalog] = useState([]);
  const [f, setF] = useState({ product_id: "", price: "", old_price: "", stock: 10, name: "", brand: "", category: CATEGORIES[0].key, image: "", unit: "" });
  useEffect(() => { if (open) api.get("/products", { params: { limit: 100 } }).then(({ data }) => setCatalog(data.filter((p) => !existing.includes(p.id)))); }, [open, existing]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const base = { price: Number(f.price), old_price: f.old_price ? Number(f.old_price) : null, stock: Number(f.stock) };
    const body = mode === "existing" ? { ...base, product_id: f.product_id }
      : { ...base, new_product: { name: f.name, brand: f.brand, category: f.category, image: f.image || null, unit: f.unit } };
    try { await api.post("/store/offers", body); toast.success("Produto adicionado à sua loja"); setOpen(false); onSaved(); }
    catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className={btnPrimary} data-testid="add-offer-btn"><Plus className="h-4 w-4" />Adicionar produto</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Adicionar produto à loja</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div className="flex gap-2">
            {[["existing", "Produto do catálogo"], ["new", "Novo produto"]].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setMode(v)} data-testid={`add-offer-mode-${v}`}
                className={`rounded-full px-4 py-1.5 text-sm font-medium ${mode === v ? "bg-bo-green text-white" : "bg-gray-100"}`}>{l}</button>))}
          </div>
          {mode === "existing" ? (
            <Field label="Produto"><select required value={f.product_id} onChange={set("product_id")} className={inputCls} data-testid="add-offer-product-select">
              <option value="">Selecionar...</option>{catalog.map((p) => <option key={p.id} value={p.id}>{p.name} (desde {fmt(p.min_price)})</option>)}</select></Field>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Nome"><input required minLength={2} value={f.name} onChange={set("name")} className={inputCls} data-testid="add-offer-name-input" /></Field></div>
              <Field label="Marca"><input value={f.brand} onChange={set("brand")} className={inputCls} data-testid="add-offer-brand-input" /></Field>
              <Field label="Categoria"><select value={f.category} onChange={set("category")} className={inputCls} data-testid="add-offer-category-select">{CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}</select></Field>
              <Field label="Unidade"><input value={f.unit} onChange={set("unit")} placeholder="1 kg" className={inputCls} data-testid="add-offer-unit-input" /></Field>
              <Field label="URL da imagem (https)"><input type="url" value={f.image} onChange={set("image")} className={inputCls} data-testid="add-offer-image-input" /></Field>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Preço (STN)"><input type="number" required min={0.01} step="0.01" value={f.price} onChange={set("price")} className={inputCls} data-testid="add-offer-price-input" /></Field>
            <Field label="Preço anterior"><input type="number" min={0} step="0.01" value={f.old_price} onChange={set("old_price")} className={inputCls} data-testid="add-offer-oldprice-input" /></Field>
            <Field label="Stock"><input type="number" required min={0} value={f.stock} onChange={set("stock")} className={inputCls} data-testid="add-offer-stock-input" /></Field>
          </div>
          <button className={btnPrimary} data-testid="add-offer-submit-btn">Adicionar</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StoreOrders({ orders, reload }) {
  const act = async (id, status) => {
    try { await api.patch(`/store/orders/${id}/fulfillment`, { status }); toast.success("Estado atualizado"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!orders.length) return <p className="bo-card p-8 text-center text-sm text-gray-500">Ainda não há encomendas.</p>;
  return (
    <div className="flex flex-col gap-3">
      {orders.map((o) => {
        const canWork = ["confirmed", "preparing", "ready"].includes(o.status);
        return (
          <div key={o.id} className="bo-card p-4" data-testid={`store-order-${o.number}`}>
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-bold">{o.number}</p><StatusBadge status={o.status} />
              <span className="text-xs text-gray-500">{fmtDate(o.created_at)} · {PAY_METHOD[o.payment_method]} · Cliente: {o.customer_name}</span>
              <span className="ml-auto font-bold">{fmt(o.store_total)}</span>
            </div>
            <ul className="mt-2 text-sm text-gray-600">{o.items.map((i) => <li key={i.offer_id}>{i.qty} × {i.name}</li>)}</ul>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs">A sua parte: <strong data-testid="store-fulfillment-status">{FULFILL[o.fulfillment]}</strong></span>
              {!canWork && o.status === "pending_payment" && <span className="text-xs text-amber-700">Aguarda validação do pagamento</span>}
              {canWork && o.fulfillment === "pending" && <button onClick={() => act(o.id, "preparing")} className={`${btnGhost} px-3 py-1.5`} data-testid={`store-prepare-btn-${o.number}`}>Iniciar preparação</button>}
              {canWork && o.fulfillment !== "ready" && <button onClick={() => act(o.id, "ready")} className={`${btnPrimary} px-3 py-1.5`} data-testid={`store-ready-btn-${o.number}`}>Marcar pronta</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function StorePanel() {
  const [store, setStore] = useState(null);
  const [offers, setOffers] = useState([]);
  const [orders, setOrders] = useState([]);
  const load = () => {
    api.get("/store/me").then(({ data }) => setStore(data)).catch((e) => { toast.error(errMsg(e)); setStore(false); });
    api.get("/store/offers").then(({ data }) => setOffers(data)).catch(() => {});
    api.get("/store/orders").then(({ data }) => setOrders(data)).catch(() => {});
  };
  useEffect(load, []);
  if (store === null) return <Spinner />;
  if (!store) return <p className="bo-card p-8 text-center">A sua loja não está ativa.</p>;
  return (
    <div data-testid="store-panel">
      <PageTitle title={store.name} sub={`Painel da loja · ${store.location}`} />
      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="overview" data-testid="store-tab-overview">Resumo</TabsTrigger>
          <TabsTrigger value="offers" data-testid="store-tab-offers">Produtos e stock</TabsTrigger>
          <TabsTrigger value="orders" data-testid="store-tab-orders">Encomendas</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="store-tab-onboarding">Dados legais e documentos</TabsTrigger>
        </TabsList>
        <TabsContent value="onboarding"><StoreOnboarding /></TabsContent>
        <TabsContent value="overview">
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat icon={Package} label="Produtos à venda" value={store.stats.offers} testid="store-stat-offers" />
            <Stat icon={AlertTriangle} label="Stock baixo (≤5)" value={store.stats.low_stock} testid="store-stat-low" />
            <Stat icon={ClipboardList} label="Encomendas por preparar" value={store.stats.orders_to_prepare} testid="store-stat-orders" />
          </div>
        </TabsContent>
        <TabsContent value="offers">
          <div className="mb-3 flex justify-end"><AddOffer onSaved={load} existing={offers.map((o) => o.product_id)} /></div>
          <div className="bo-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500"><tr><th className="py-2 pl-4 text-left">Produto</th><th className="text-left">Preço</th><th className="text-left">Preço anterior</th><th className="text-left">Stock</th><th>Ativo</th><th /></tr></thead>
              <tbody>{offers.map((o) => <OfferRow key={o.id} o={o} onSaved={load} storeId={store.id} />)}</tbody>
            </table>
          </div>
        </TabsContent>
        <TabsContent value="orders"><StoreOrders orders={orders} reload={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
